import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { getLastCameraDeviceId, setLastCameraDeviceId } from "utils/storage.ts";

export interface CameraDevice {
  deviceId: string;
  label: string;
}

export interface ScanResult {
  text: string;
  format: string;
}

export interface UseCameraScannerOptions {
  onScan?: (result: ScanResult) => void;
  onError?: (error: string) => void;
  autoStart?: boolean;
  enableDeviceSelection?: boolean;
  persistDeviceSelection?: boolean;
}

export function useCameraScanner(options: UseCameraScannerOptions = {}) {
  const {
    onScan,
    onError,
    autoStart = false,
    enableDeviceSelection = false,
    persistDeviceSelection = false,
  } = options;

  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [permissionState, setPermissionState] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [needsPermission, setNeedsPermission] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<any>(null);
  const isInitializingRef = useRef(false);

  // Handle video element changes (for tab switching)
  const handleVideoElementChange = useCallback((newVideoElement: HTMLVideoElement) => {
    console.log('Video element changed, checking if restart needed...');
    
    // Check if the scanner is already using this video element
    const currentVideoElement = videoRef.current;
    if (currentVideoElement === newVideoElement && controlsRef.current && scanning) {
      console.log('Scanner already using this video element, skipping restart');
      return;
    }
    
    console.log('Restarting scanner for new video element...');
    
    // If we were scanning, restart scanning with the new element
    if (scanning && selectedDeviceId) {
      // Stop current scanning first
      if (controlsRef.current) {
        try {
          controlsRef.current.stop();
        } catch (err) {
          console.warn('Error stopping scanner controls:', err);
        }
        controlsRef.current = null;
      }
      
      // Start scanning with the new video element
      setTimeout(async () => {
        try {
          setError("");
          setScanning(true);

          // Clean up any existing reader
          if (codeReaderRef.current) {
            codeReaderRef.current = null;
          }

          const codeReader = new BrowserMultiFormatReader();
          codeReaderRef.current = codeReader;

          const controls = await codeReader.decodeFromVideoDevice(
            selectedDeviceId,
            newVideoElement, // Use the new video element directly
            (result, err) => {
              if (result) {
                const scanResult: ScanResult = {
                  text: result.getText(),
                  format: result.getBarcodeFormat()?.toString() || 'unknown',
                };
                onScan?.(scanResult);
              }
              if (err && !result) {
                // Only set error for real exceptions, not just 'not found' or format detection
                if (!err.name.includes('NotFoundException') && 
                    !err.name.includes('NoMultiFormatReader') &&
                    !err.name.includes('FormatException') &&
                    !(err.message && err.message.includes('No MultiFormat Readers were able to detect'))) {
                  const errorMsg = err.message ? `${err.message} (${err.name})` : `Scanning error (${err.name})`;
                  setError(errorMsg);
                  onError?.(errorMsg);
                }
              }
            }
          );
          
          controlsRef.current = controls;
        } catch (err: any) {
          const errorMsg = err.message ? `${err.message} (${err.name})` : `Failed to restart camera (${err.name})`;
          setError(errorMsg);
          onError?.(errorMsg);
          setScanning(false);
        }
      }, 100); // Small delay to ensure video element is ready
    }
  }, [scanning, selectedDeviceId, onScan, onError]);

  // Check camera permissions without requesting them
  const checkPermissions = useCallback(async () => {
    try {
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
        setPermissionState(permission.state);
        
        if (permission.state === 'granted') {
          setNeedsPermission(false);
          return true;
        } else if (permission.state === 'denied') {
          setNeedsPermission(false);
          setError("Camera permission was denied. Please enable camera access in your browser settings and refresh the page.");
          return false;
        } else {
          setNeedsPermission(true);
          return false;
        }
      } else {
        // Fallback for browsers that don't support permissions API
        setNeedsPermission(true);
        return false;
      }
    } catch (err) {
      console.warn("Could not check camera permissions:", err);
      setNeedsPermission(true);
      return false;
    }
  }, []);

  // Request camera permissions and initialize devices
  const requestPermissionAndInitialize = useCallback(async (retryCount = 0) => {
    if (isInitializingRef.current) return;
    
    try {
      isInitializingRef.current = true;
      setIsInitializing(true);
      setError("");
      setNeedsPermission(false);

      await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Use BrowserMultiFormatReader to get devices
      const videoDevices = await BrowserMultiFormatReader.listVideoInputDevices();
      
      if (videoDevices.length === 0) {
        throw new Error("No camera devices found");
      }
      
      const devices = videoDevices.map((device: any) => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${device.deviceId.slice(0, 8)}...`,
      }));
      
      setDevices(devices);
      
      // Set initial device selection
      let initialDeviceId = devices[0].deviceId;
      
      if (enableDeviceSelection && persistDeviceSelection) {
        const storedId = getLastCameraDeviceId();
        const found = devices.find((d: any) => d.deviceId === storedId);
        if (storedId && found) {
          initialDeviceId = storedId;
        }
      }
      
      setSelectedDeviceId(initialDeviceId);
      setIsInitialized(true);
      setPermissionState('granted');
      setIsInitializing(false);
      isInitializingRef.current = false;
      
    } catch (err: any) {
      setIsInitializing(false);
      isInitializingRef.current = false;
      
      // If it's a permission error and we haven't retried too many times, try again
      if (retryCount < 2 && (err.name === 'NotAllowedError' || err.name === 'NotFoundError')) {
        console.log(`Camera initialization failed, retrying... (${retryCount + 1}/3)`);
        setTimeout(() => requestPermissionAndInitialize(retryCount + 1), 1000);
        return;
      }
      
      let errorMsg = "Could not access camera. Please check permissions.";
      
      if (err.name === 'NotAllowedError') {
        errorMsg = `Camera permission denied. Please allow camera access and try again.`;
        setPermissionState('denied');
        setNeedsPermission(true);
      } else if (err.name === 'NotFoundError') {
        errorMsg = `No camera found on this device (${err.name}).`;
      } else if (err.name === 'NotSupportedError') {
        errorMsg = `Camera access is not supported in this browser (${err.name}).`;
      } else if (err.name === 'NotReadableError' || err.name === 'AbortError') {
        errorMsg = `Camera is currently in use by another application (${err.name}). Please close other camera apps and try again.`;
      } else if (err.name === 'OverconstrainedError') {
        errorMsg = `Camera doesn't meet the required specifications (${err.name}). Please try a different camera.`;
      } else if (err.message) {
        errorMsg = `${err.message} (${err.name})`;
      } else {
        errorMsg = `Unknown camera error (${err.name})`;
      }
      
      setError(errorMsg);
      onError?.(errorMsg);
    }
  }, [enableDeviceSelection, persistDeviceSelection, onError]);

  // Initialize on mount - check permissions first
  useEffect(() => {
    const initializeCamera = async () => {
      const hasPermission = await checkPermissions();
      if (hasPermission) {
        // If we already have permission, initialize automatically
        await requestPermissionAndInitialize();
      }
    };
    
    initializeCamera();
  }, [checkPermissions, requestPermissionAndInitialize]);

  // Save device selection to localStorage when changed
  useEffect(() => {
    if (selectedDeviceId && persistDeviceSelection) {
      setLastCameraDeviceId(selectedDeviceId);
    }
  }, [selectedDeviceId, persistDeviceSelection]);

  // Auto-start scanning if enabled
  useEffect(() => {
    if (autoStart && isInitialized && selectedDeviceId && !scanning && !isInitializing) {
      startScanning();
    }
  }, [autoStart, isInitialized, selectedDeviceId, scanning, isInitializing]);

  const startScanning = async () => {
    if (!videoRef.current || !selectedDeviceId) {
      const errorMsg = "Camera not ready";
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    try {
      setError("");
      setScanning(true);

      // Clean up any existing reader
      if (codeReaderRef.current) {
        codeReaderRef.current = null;
      }

      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      const controls = await codeReader.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        (result, err) => {
          if (result) {
            const scanResult: ScanResult = {
              text: result.getText(),
              format: result.getBarcodeFormat()?.toString() || 'unknown',
            };
            onScan?.(scanResult);
            // Don't stop scanning automatically - let user control it
          }
          if (err && !result) {
            // Only set error for real exceptions, not just 'not found' or format detection
            if (!err.name.includes('NotFoundException') && 
                !err.name.includes('NoMultiFormatReader') &&
                !err.name.includes('FormatException') &&
                !(err.message && err.message.includes('No MultiFormat Readers were able to detect'))) {
              const errorMsg = err.message ? `${err.message} (${err.name})` : `Scanning error (${err.name})`;
              setError(errorMsg);
              onError?.(errorMsg);
            }
          }
        }
      );
      
      controlsRef.current = controls;
    } catch (err: any) {
      const errorMsg = err.message ? `${err.message} (${err.name})` : `Failed to start camera (${err.name})`;
      setError(errorMsg);
      onError?.(errorMsg);
      setScanning(false);
    }
  };

  const stopScanning = useCallback(() => {
    if (controlsRef.current) {
      try {
        controlsRef.current.stop();
      } catch (err) {
        console.warn('Error stopping scanner controls:', err);
      }
      controlsRef.current = null;
    }
    if (codeReaderRef.current) {
      codeReaderRef.current = null;
    }
    setScanning(false);
  }, []);

  const changeDevice = useCallback((deviceId: string) => {
    if (scanning) {
      stopScanning();
    }
    setSelectedDeviceId(deviceId);
  }, [scanning, stopScanning]);

  const retryInitialization = useCallback(() => {
    setError("");
    setIsInitialized(false);
    setIsInitializing(false);
    isInitializingRef.current = false;
    requestPermissionAndInitialize();
  }, [requestPermissionAndInitialize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  return {
    // State
    devices,
    selectedDeviceId,
    scanning,
    error,
    isInitialized,
    isInitializing,
    permissionState,
    needsPermission,
    
    // Refs
    videoRef,
    
    // Actions
    startScanning,
    stopScanning,
    changeDevice,
    setError,
    retryInitialization,
    requestPermissionAndInitialize,
    handleVideoElementChange,
  };
} 